import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import * as venn from "venn.js";

const DynamicVennDiagram = ({
	data = [],
	colors = {},
	size = { width: 600, height: 500 },
	formatDataFun = (row) => ({ sets: [], size: 100 }),
	hidelabel = false,
}) => {
	const chartRef = useRef(null);

	useEffect(() => {
		if (!data?.length) return;

		const formattedData = data.map(formatDataFun);
		const chart = venn.VennDiagram().width(size.width).height(size.height);

		// Clear previous SVG
		d3.select(chartRef.current).select("svg").remove();

		const div = d3.select(chartRef.current).datum(formattedData).call(chart);

		// Tooltip setup
		const tooltip = d3
			.select("body")
			.append("div")
			.attr("class", "venntooltip")
			.style("position", "absolute")
			.style("text-align", "center")
			.style("padding", "8px 12px")
			.style("font", "12px sans-serif")
			.style("background", "rgba(0,0,0,0.85)")
			.style("color", "white")
			.style("border-radius", "6px")
			.style("pointer-events", "none")
			.style("opacity", 0);

		const blendColors = (sets) => {
			if (sets.length === 1) return colors[sets[0]] || "#999";
			const intersectionKey = sets.join("∩");
			if (colors[intersectionKey]) return colors[intersectionKey];
			const hexToRgb = (hex) =>
				hex
					.replace("#", "")
					.match(/.{1,2}/g)
					.map((x) => parseInt(x, 16));
			const avg = sets
				.map((s) => hexToRgb(colors[s] || "#999"))
				.reduce((a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]], [0, 0, 0])
				.map((v) => Math.round(v / sets.length));
			return `rgb(${avg[0]}, ${avg[1]}, ${avg[2]})`;
		};

		// Circle + event setup
		div.selectAll("g")
			.each(function (d) {
				const color = blendColors(d.sets);
				d3.select(this)
					.select("path")
					.style("fill", color)
					.style("fill-opacity", 0.9)
					.style("stroke", "#fff")
					.style("stroke-opacity", 0)
					.style("transition", "all 0.3s ease");
			})
			.on("mouseover", function (event, d) {
				venn.sortAreas(div, d);
				d3.select(this).select("path").style("stroke-opacity", 1).style("stroke-width", 2).style("fill-opacity", 1);

				tooltip.transition().duration(150).style("opacity", 1);
				tooltip
					.html(`<b>${d.sets.join(" ∩ ")}</b><br/>Customers: ${d.size}`)
					.style("left", event.pageX + 10 + "px")
					.style("top", event.pageY - 30 + "px");
			})
			.on("mousemove", function (event) {
				tooltip.style("left", event.pageX + 10 + "px").style("top", event.pageY - 30 + "px");
			})
			.on("mouseout", function () {
				d3.select(this).select("path").style("stroke-opacity", 0).style("stroke-width", 0).style("fill-opacity", 0.9);
				tooltip.transition().duration(300).style("opacity", 0);
			});

		// Text labels
		const texts = div.selectAll("text");
		texts.style("fill", "#fff").style("font-size", "14px").style("font-weight", "600").style("pointer-events", "none");

		// 👉 Hide labels if `hidelabel` is true
		if (hidelabel) {
			texts.style("display", "none");
		}

		return () => {
			d3.select(chartRef.current).select("svg").remove();
			tooltip.remove();
		};
	}, [data, colors, size, formatDataFun, hidelabel]);

	return (
		<div
			ref={chartRef}
			style={{
				textAlign: "center",
				width: size.width,
				margin: "0 auto",
			}}
		/>
	);
};

export default DynamicVennDiagram;
